import {Injectable} from '@angular/core';
import {
  AuditLogPage,
  AuditLogsService,
  EventAction,
  EventActionDescription,
  EventActionStatus,
  EventLocationType,
  EventManagers,
  EventPost,
  EventPricing,
  EventSaleConfiguration,
  EventSalespoint,
  EventSalespointPut,
  EventStatusType,
  EventWarningType,
  PrintTicketType,
  SelfServiceEvent,
  SelfServiceEventPage,
  SelfServiceService,
  TicketTypeDelete,
  TicketTypeGet,
  TicketTypePost,
  VenueGet,
  VenuePost,
  VenuePut,
} from '../../../generated/sales-terminal-client';
import {AuthService} from '../../auth/auth.service';
import {Observable, tap} from 'rxjs';
import {SnackService} from '../../snack/snack.service';
import {PageableSearch} from '../../../components/scroll-load-component/scroll-load.component';
import {finalize} from 'rxjs/operators';
import {Router} from "@angular/router";
import {LanguageService} from '../../language/language.service';
import {SelfServiceEventBaseService} from './self-service-event-base.service';
import {SelfServiceEventInterface} from './self-service-event.interface';
import {SelfServiceVenueService} from '../venue/self-service-venue.service';
import {FileData, UtilService} from '../../util/util.service';

export interface SelfServiceSearchData extends PageableSearch {
  l: string;
  q?: string;
  publishedStart?: string;
  publishedEnd?: string;
  eventStart?: string;
  eventEnd?: string;
  saleStart?: string;
  saleEnd?: string;
  legalPersonId?: number;
  venueId?: number;
  locationType?: EventLocationType;
  statusType?: EventStatusType;
}

@Injectable()
export class SelfServiceEventService extends SelfServiceEventBaseService implements SelfServiceEventInterface {
  constructor(private _api: SelfServiceService,
              private snackService: SnackService,
              private router: Router,
              private venueService: SelfServiceVenueService,
              private auditLogsService: AuditLogsService,
              private utilService: UtilService,
              protected authService: AuthService,
              protected languageService: LanguageService) {
    super(authService, languageService);
    this.isSandbox = false;
  }

  public updateTicketTypeList(event: SelfServiceEvent = this.activeEvent, action?: Function): void {
    this.getEventTicketTypes(event.id).pipe(finalize(() => {
      if (typeof action === 'function') {
        action();
      }
    })).subscribe((ticketTypes: TicketTypeGet[]) => {
      this.eventTicketTypeList = this.sortTicketTypes(ticketTypes);
      this.validateTicketTypeRange();
    });
  }

  public search(data: SelfServiceSearchData): Observable<SelfServiceEventPage> {
    return this._api.searchSelfServiceEvents(
      data.l,
      data.q,
      data.publishedStart,
      data.publishedEnd,
      data.eventStart,
      data.eventEnd,
      data.saleStart,
      data.saleEnd,
      data.legalPersonId,
      data.venueId,
      data.locationType,
      data.statusType,
      data.page,
      data.pageSize
    ).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public createEvent(eventPost: EventPost): Observable<SelfServiceEvent> {
    return this._api.createEvent(eventPost).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public getEvent(eventId: number): Observable<SelfServiceEvent> {
    return this._api.findSelfServiceById(eventId).pipe(
      tap({error: (e: any) => {
          this.snackService.showBackendError(e)
          if (e?.error?.message === 'api_events_get_event_unauthorized') {
            this.router.navigate([this.authService.accessDeniedRoute]);
          }
        }})
    );
  }

  public updateEvent(eventId: number, eventPost: EventPost): Observable<SelfServiceEvent> {
    return this._api.updateEvent(eventId, eventPost).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public deleteEvent(eventId: number): Observable<any> {
    return this._api.selfServiceEventIdDelete(eventId).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public getEventTicketTypes(eventId: number): Observable<TicketTypeGet[]> {
    return this._api.selfServiceEventIdTicketTypesGet(eventId).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public addEventTicketType(eventId: number, ticketType: TicketTypePost): Observable<TicketTypeGet> {
    return this._api.selfServiceEventIdTicketTypePost(eventId, ticketType).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public updateEventTicketType(eventId: number, ticketTypeUuid: string, ticketType: TicketTypePost): Observable<TicketTypeGet> {
    return this._api.selfServiceEventIdTicketTypeUuidPut(eventId, ticketTypeUuid, ticketType).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public deleteEventTicketType(eventId: number, ticketTypeUuid: string): Observable<TicketTypeDelete> {
    return this._api.selfServiceEventIdTicketTypeUuidDelete(eventId, ticketTypeUuid).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public getEventStatusWarnings(eventId: number, action: EventAction, newVenueId: number = null): Observable<EventWarningType[]> {
    return this._api.getEventStatusWarnings(eventId, action, newVenueId).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public updateEventAction(eventId: number, action: EventAction, eventActionDescription?: EventActionDescription): Observable<EventActionStatus> {
    return this._api.selfServiceEventIdActionActionPut(eventId, action, eventActionDescription).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public copyEvent(eventId: number, eventStart: string, saleStart: string): Observable<SelfServiceEvent> {
    return this._api.copyEvent(eventId, eventStart, saleStart).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public addPromocode(eventId: number, uuid: string, code: string): Observable<TicketTypeGet> {
    return this._api.selfServiceEventIdTicketTypeUuidPromocodePost(eventId, uuid, '"' + code + '"').pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public removePromocode(eventId: number, uuid: string, code: string): Observable<TicketTypeGet> {
    return this._api.selfServiceEventIdTicketTypeUuidPromocodePut(eventId, uuid, '"' + code + '"').pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public getEventPricing(eventId: number): Observable<EventPricing> {
    return this._api.selfServiceEventIdPricesGet(eventId).pipe(
      tap({error: (e: any) => {
          this.snackService.showBackendError(e);
          if (e?.error?.message === 'api_events_event_not_found') {
            this.router.navigate([this.authService.accessDeniedRoute]);
          }
        }})
    );
  }

  public getEventManagers(eventId: number): Observable<EventManagers> {
    return this._api.selfServiceEventIdManagersGet(eventId).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public addEventManagers(eventId: number, managerIds: string[]): Observable<EventManagers> {
    return this._api.selfServiceEventIdManagersPost(eventId, managerIds).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public removeEventManagers(eventId: number, managerIds: string[]): Observable<EventManagers> {
    return this._api.selfServiceEventIdManagersPut(eventId, managerIds).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public updateEventOwner(eventId: number, newOwnerCentreId: number) {
    return this._api.selfServiceEventIdOwnerPut(eventId, newOwnerCentreId).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    )
  }

  public resetEventOwner(): void {
    this.updatedOwnerCentreId = this.activeEvent.ownerCentreId;
    this.changeTracker.ownerChanged = false;
  }

  public updateSaleConfiguration(eventId: number, conf: EventSaleConfiguration): Observable<SelfServiceEvent> {
    return this._api.updateSaleConfiguration(eventId, conf).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public checkForWarnings(): void {
    if (this.activeEvent?.id) {
      this.getEventStatusWarnings(this.activeEvent.id, EventAction.Publish, this.activeEvent.venueId).subscribe((warnings) => {
        this.activeWarnings = [];
        this.checkIfPrivatePerson();
        if (warnings?.length) {
          warnings.forEach((warning, index) => {
            this.activeWarnings.push('general.api_' + warning.toLowerCase());
          });
        }
        this.checkConsentWarning();
        this.warningWatcher.next(new Date());
      });
    } else {
      this.activeWarnings = [];
      this.checkIfPrivatePerson();
      this.checkConsentWarning();
      this.warningWatcher.next(new Date());
    }
  }

  public getVenue(id: number): Observable<VenueGet> {
    return this.venueService.get(id);
  }
  public createVenue(data: VenuePost): Observable<VenueGet> {
    return this.venueService.create(data);
  }
  public updateVenue(id: number, data: VenuePut): Observable<VenueGet> {
    return this.venueService.update(id, data);
  }

  public previewTicketTypePDF(ticketTypeUuid: string): void {
    this._api.getEventTicketTypePreview(this.activeEvent.id, ticketTypeUuid).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    ).subscribe((blob: Blob) => {
      const fileData: FileData = UtilService.getFileData('', PrintTicketType.Pdf);
      const file: Blob = new Blob([blob], { type: fileData.mime });
      const pdf_filename: string = 'pdf_ticket_preview_event' + this.activeEvent.id + '_type' + ticketTypeUuid;
      if (this.utilService.isMobile) {
        this.utilService.mobileDownload(file, pdf_filename + '.pdf');
      } else {
        this.utilService.previewOrPrintFile(file, null, null, PrintTicketType.Pdf, pdf_filename);
      }
    })
  }

  public getAuditLogs(id: number, page: number, pageSize: number): Observable<AuditLogPage> {
    return this.auditLogsService.getEventAuditLogsByEventId(id, page, pageSize).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getEventSalesAndRefundRights(eventId: number): Observable<Array<EventSalespoint>>{
    return this._api.selfServiceEventIdSalespointsGet(eventId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public saveSalesAndRefundRights(eventId: number, spPutList: EventSalespointPut[] = []): Observable<Array<EventSalespoint>> {
    return this._api.selfServiceEventIdSalespointsPut(eventId, spPutList).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
}
